/**
 * SolutionBlockerAnswers — barrier Q&A (or AI-merged paragraph) in the report.
 *
 * Prefers ai_analysis.blockerNarrative.text; falls back to the frozen answers[]
 * list when generation failed or has not run. Returns null when there is
 * nothing to show. Curator edits use report_edits via EditableText.
 */
import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useReportEdits } from './ReportEditsContext';
import EditableText from './EditableText';

const Wrap = styled.div`
  margin-top: 14px;
`;

const Title = styled.h5`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
`;

const LeadIn = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
  margin: 10px 0 0;
`;

const FallbackNote = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  margin: 0 0 10px;
`;

const List = styled.ol`
  margin: 0;
  padding-left: 18px;
`;

const Item = styled.li`
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.55;
  color: #374151;
`;

const Question = styled.div`
  font-weight: 600;
  color: #111827;
`;

const Answer = styled.div`
  margin-top: 4px;
  color: #4b5563;
  white-space: pre-wrap;
`;

const AnswerLabel = styled.span`
  font-weight: 600;
  color: #6b7280;
`;

export default function SolutionBlockerAnswers({
  solutionId,
  narrative,
  variant = 'full',
}) {
  const { t } = useTranslation();
  const { get, isEdited } = useReportEdits();
  const fieldKey = solutionId ? `solution.${solutionId}.blockerNarrative` : null;
  const text = (narrative?.text || '').trim();
  const answers = Array.isArray(narrative?.answers) ? narrative.answers.filter((a) => a?.answer?.trim()) : [];
  const editedText = fieldKey ? String(get(fieldKey, text) || '').trim() : text;
  const hasEdit = Boolean(fieldKey && isEdited(fieldKey));

  if (variant === 'lead-in') {
    const lead = (hasEdit ? editedText : text).trim();
    if (!lead) return null;
    return (
      <LeadIn>
        {fieldKey
          ? <EditableText fieldKey={fieldKey} fallback={text} label={t('report.solutions.blockerNarrativeLabel')} />
          : lead}
      </LeadIn>
    );
  }

  if (hasEdit || text) {
    return (
      <Wrap>
        <Title>{t('report.solutions.blockerAnswersTitle')}</Title>
        {fieldKey
          ? <EditableText fieldKey={fieldKey} fallback={text} label={t('report.solutions.blockerNarrativeLabel')} />
          : <p>{hasEdit ? editedText : text}</p>}
      </Wrap>
    );
  }

  if (!answers.length) return null;

  return (
    <Wrap>
      <Title>{t('report.solutions.blockerAnswersTitle')}</Title>
      <FallbackNote>{t('report.solutions.blockerFallbackNote')}</FallbackNote>
      <List>
        {answers.map((a) => (
          <Item key={a.itemId || a.question}>
            {a.question ? (
              <Question>
                {a.question}
                {a.lens ? ` · ${a.lens}` : ''}
              </Question>
            ) : null}
            <Answer>
              <AnswerLabel>{t('report.solutions.blockerAnswerLabel')}: </AnswerLabel>
              {a.answer}
            </Answer>
          </Item>
        ))}
      </List>
    </Wrap>
  );
}
